import fs from 'node:fs'
import path from 'node:path'
import type { ResolvedBetterstartConfig } from '@cli-types/core-engine/config.js'
import { DEFAULT_ADMIN_NAMESPACE } from '@cli-constants/scaffolding.js'
import { findConfigFile } from '@core-engine/config/resolver/find-config-file.js'
import { loadConfigFile } from '@core-engine/config/resolver/load-config-file.js'
import { detectNextMajorVersion } from '@next-adapter/config/detect/detect-next-major-version.js'
import { deriveNamespacePaths } from '@next-adapter/config/namespace/derive-namespace-paths.js'
import { validateAdminNamespace } from '@next-adapter/config/namespace/validate-admin-namespace.js'
import { assertNextConfigShape } from './assert-next-config-shape.js'
import { getDefaultConfig } from './get-default-config.js'

export async function resolveConfig(cwd?: string): Promise<ResolvedBetterstartConfig> {
  const workingDir = cwd ?? process.cwd()
  const configPath = findConfigFile(workingDir)
  const nextMajorVersion = detectNextMajorVersion(workingDir)
  const hasSrc = fs.existsSync(path.join(workingDir, 'src'))
  const defaultConfig = getDefaultConfig(hasSrc, nextMajorVersion)

  if (!configPath) {
    return defaultConfig
  }

  const loadedConfig = await loadConfigFile(configPath)
  assertNextConfigShape(loadedConfig)
  const loadedNextConfig = loadedConfig.frameworkConfig.next
  const loadedDatabase = loadedConfig.database
  const namespace = validateAdminNamespace(loadedNextConfig.namespace ?? DEFAULT_ADMIN_NAMESPACE)
  const nextPaths = {
    ...deriveNamespacePaths(namespace, loadedNextConfig.srcDir ?? defaultConfig.srcDir),
    ...loadedNextConfig.paths
  }
  const nextConfig = {
    ...defaultConfig.frameworkConfig.next,
    ...loadedNextConfig,
    namespace,
    version: nextMajorVersion,
    paths: nextPaths
  }
  const config: ResolvedBetterstartConfig = {
    ...defaultConfig,
    ...loadedConfig,
    framework: 'next',
    frameworkConfig: {
      next: nextConfig
    },
    srcDir: nextConfig.srcDir,
    nextMajorVersion,
    paths: nextPaths,
    database: {
      provider: 'postgres',
      migrationsDir: loadedDatabase.migrationsDir
    },
    presets: loadedConfig.presets ?? defaultConfig.presets,
    integrations: loadedConfig.integrations ?? defaultConfig.integrations,
    email: loadedConfig.email ?? defaultConfig.email,
    storage: loadedConfig.storage ?? defaultConfig.storage,
    generated: {
      ...defaultConfig.generated,
      ...loadedConfig.generated
    }
  }
  return config
}
